import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  WhatsApp as WhatsAppIcon,
  Refresh as RefreshIcon,
  GetApp as DownloadIcon,
} from "@mui/icons-material";
import AdminApi from "Service/Admin.api";
import { Button } from "@mui/material";

const WhatsAppDashboard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState<"logs" | "users" | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await AdminApi.getWhatsAppChatLogs(page + 1, rowsPerPage, search);
      if (res.success) {
        setData(res.data.data);
        setTotalCount(res.data.totalCount);
      }
    } catch (error) {
      console.error("Error fetching WhatsApp chat logs:", error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleExportLogs = async () => {
    setExporting("logs");
    try {
      const res = await AdminApi.exportWhatsAppChatLogs();
      if (res.success && res.data) {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `WhatsAppChatLogs_${new Date().toISOString().split('T')[0]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
      }
    } catch (error) {
      console.error("Error exporting chat logs:", error);
    } finally {
      setExporting(null);
    }
  };

  const handleExportUsers = async () => {
    setExporting("users");
    try {
      const res = await AdminApi.exportWhatsAppUsers();
      if (res.success && res.data) {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `WhatsAppUsers_${new Date().toISOString().split('T')[0]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
      }
    } catch (error) {
      console.error("Error exporting users:", error);
    } finally {
      setExporting(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold" sx={{ color: "primary.main", display: "flex", alignItems: "center", gap: 1 }}>
          <WhatsAppIcon fontSize="large" /> WhatsApp Chat Logs
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <Button
            variant="contained"
            color="success"
            startIcon={exporting === "logs" ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            onClick={handleExportLogs}
            disabled={exporting !== null || loading}
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            Export Logs
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={exporting === "users" ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            onClick={handleExportUsers}
            disabled={exporting !== null || loading}
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            Export Users
          </Button>
          <Tooltip title="Refresh Data">
            <IconButton onClick={() => fetchData()} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Paper sx={{ width: "100%", mb: 2, borderRadius: 2, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
        <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end", bgcolor: "#f8f9fa" }}>
          <TextField
            size="small"
            placeholder="Search by phone, name or query..."
            value={search}
            onChange={handleSearchChange}
            sx={{ width: 350, bgcolor: "white" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <TableContainer sx={{ maxHeight: "calc(100vh - 320px)", overflow: "auto" }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={400}>
              <CircularProgress />
            </Box>
          ) : (
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "#f1f3f5" }}>Phone Number</TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "#f1f3f5" }}>User Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "#f1f3f5" }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "#f1f3f5" }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "#f1f3f5" }}>User Query</TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "#f1f3f5" }}>Bot Response</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length > 0 ? (
                  data.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.phoneNumber}</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>{row.fullName}</TableCell>
                      <TableCell>{row.email || "N/A"}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{new Date(row.createdAt).toLocaleString()}</TableCell>
                      <TableCell sx={{ maxWidth: 250, wordWrap: "break-word" }}>{row.userQuery}</TableCell>
                      <TableCell sx={{ maxWidth: 250, wordWrap: "break-word" }}>{row.botResponse}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                      <Typography color="textSecondary">No data found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default WhatsAppDashboard;
